import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { ReportHeader, ReportSummaryCard } from "../../components/reports";

const fmt = (n) =>
  n != null && n !== "" ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = d.toString().split("T")[0];
  const [y, m, dd] = dt.split("-");
  return `${dd}-${m}-${y}`;
};

// Column definitions: { key, label }
const COLUMNS = [
  { key: "sno",               label: "S No",                  align: "center" },
  { key: "region",            label: "Region",                align: "left"   },
  { key: "division",          label: "Division",              align: "left"   },
  { key: "branchName",        label: "Branch Name",           align: "left"   },
  { key: "branchCode",        label: "Branch Code",           align: "left"   },
  { key: "customerName",      label: "Customer Name",         align: "left"   },
  { key: "address",           label: "Address",               align: "left"   },
  { key: "contactNo",         label: "Contact No",            align: "left"   },
  { key: "funderName1",       label: "Funder Name 1",         align: "left"   },
  { key: "funderName2",       label: "Funder Name 2",         align: "left"   },
  { key: "funderName3",       label: "Funder Name 3",         align: "left"   },
  { key: "loanAccountNo",     label: "Loan Account No",       align: "left"   },
  { key: "disbursementDate",  label: "Disbursement Date",     align: "left"   },
  { key: "maturityDate",      label: "Maturity Date",             align: "left"   },
  { key: "disbursedAmount",   label: "Disbursed Amount",          align: "right"  },
  { key: "tenureInMonths",    label: "Tenure In Months",      align: "center" },
  { key: "emiMonthsPaid",     label: "EMI Months Paid",       align: "center" },
  { key: "employeeName",      label: "Employee Name",         align: "left"   },
  { key: "employeeCode",      label: "Employee Code",         align: "center" },
  { key: "outstandingPrincipal", label: "Outstanding Principal", align: "right"  },
  { key: "outstandingInterest",  label: "Outstanding Interest",  align: "right"  },
  { key: "principalPaid",     label: "Principal Paid",        align: "right"  },
  { key: "interestPaid",      label: "Interest Paid",             align: "right"  },
  { key: "overduePrincipal",  label: "Overdue Principal",     align: "right"  },
  { key: "overdueInterest",   label: "Overdue Interest",      align: "right"  },
  { key: "totalPAR",          label: "Total PAR",             align: "right"  },
  { key: "dpd",               label: "DPD",                   align: "center" },
  { key: "bucket",            label: "Bucket",                align: "center" },
  { key: "lastPaidDate",      label: "Last Paid Date",        align: "left"   },
  { key: "firstOverdueDate",  label: "First Overdue Date",    align: "left"   },
  { key: "isDemised",         label: "Is Demised (Insurance)", align: "center" },
  { key: "emiStartDate",      label: "EMI Start Date",        align: "left"   },
  { key: "prospectId",        label: "Prospect ID",           align: "left"   },
  { key: "processingFee",     label: "Processing Fee",        align: "right"  },
  { key: "gst",               label: "GST",                   align: "right"  },
  { key: "insurance",         label: "Insurance",             align: "right"  },
  { key: "securityDeposit",   label: "Security Deposit",      align: "right"  },
  { key: "emiAmount",         label: "EMI Amount",            align: "right"  },
  { key: "loanPurpose",       label: "Loan Purpose",          align: "left"   },
  { key: "aadharNumber",      label: "Aadhar Number",         align: "left"   },
  { key: "panNumber",         label: "Pan Number",            align: "left"   },
  { key: "voterId",           label: "Voter Id",              align: "left"   },
  { key: "bankAccountNumber", label: "Bank Account Number",   align: "left"   },
  { key: "ifscCode",          label: "IFSC Code",             align: "left"   },
  { key: "bankName",          label: "Bank Name",             align: "left"   },
  { key: "bankBranch",        label: "Bank Branch",           align: "left"   },
];

// Columns to render as currency (₹)
const CURRENCY_KEYS = new Set([
  "disbursedAmount","outstandingPrincipal","outstandingInterest",
  "principalPaid","interestPaid","overduePrincipal","overdueInterest",
  "totalPAR","processingFee","gst","insurance","securityDeposit","emiAmount",
]);

// Columns to render as dates
const DATE_KEYS = new Set([
  "disbursementDate","maturityDate","lastPaidDate","firstOverdueDate","emiStartDate",
]);

const MasterDataReport = () => {
  const { data: authData, roleName } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (!isSuperAdmin && branchId) params.branchId = branchId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate)   params.toDate   = toDate;

      const res = await axios.get("/api/v2/master-data-report", {
        baseURL: API_BASE_URL,
        params,
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setError("Failed to load master data. Please try again.");
    }
    setLoading(false);
  }, [isSuperAdmin, branchId, fromDate, toDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Client-side search across key fields
  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.customerName || "").toLowerCase().includes(q) ||
      (r.loanAccountNo || "").toLowerCase().includes(q) ||
      (r.prospectId || "").toLowerCase().includes(q) ||
      (r.branchName || "").toLowerCase().includes(q) ||
      (r.employeeName || "").toLowerCase().includes(q) ||
      (r.contactNo || "").toString().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Summary stats
  const totalDisbursed = filtered.reduce(
    (s, r) => s + (Number(r.disbursedAmount) || 0), 0
  );
  const totalPAR = filtered.reduce(
    (s, r) => s + (Number(r.totalPAR) || 0), 0
  );
  const overdueCount = filtered.filter((r) => r.dpd > 0).length;

  // CSV Export
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => c.label);
    const csvRows = [
      headers.join(","),
      ...filtered.map((r) =>
        COLUMNS.map((c) => {
          const val = r[c.key] ?? "";
          const str = String(val).replace(/"/g, '""');
          return str.includes(",") ? `"${str}"` : str;
        }).join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "master_data_report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (r, col, idx) => {
    const val = r[col.key];
    if (col.key === "sno") return <span className="text-ink-400">{(currentPage - 1) * rowsPerPage + idx + 1}</span>;
    if (DATE_KEYS.has(col.key)) return fmtDate(val);
    if (CURRENCY_KEYS.has(col.key)) {
      if (val === "" || val == null) return "—";
      return `₹${fmt(val)}`;
    }
    if (col.key === "dpd") return val > 0 ? <span className="text-rose-600 font-bold">{val}</span> : "0";
    if (col.key === "bucket") {
      const colorClass = val === "Current" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                         val === "1-30" ? "bg-amber-50 text-amber-700 border-amber-200" :
                         "bg-rose-50 text-rose-700 border-rose-200";
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${colorClass}`}>{val || "—"}</span>;
    }
    if (col.key === "isDemised") {
      return val === "Yes"
        ? <span className="text-rose-600 font-bold">Yes</span>
        : <span className="text-ink-600">No</span>;
    }
    if (col.key === "loanAccountNo") return <code className="text-xs font-mono text-primary font-bold">{val || "—"}</code>;
    if (col.key === "customerName") return <span className="font-semibold text-ink-900">{val || "—"}</span>;
    if (col.key === "address") {
      return (
        <span className="block max-w-[280px] truncate" title={val}>
          {val || "—"}
        </span>
      );
    }
    return val !== "" && val != null ? val : "—";
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        noWrap={true}
        filters={
          <>
            {/* From Date */}
            <div className="w-[130px] mt-1 shrink-0">
              <DatePicker
                label="From Date"
                selected={fromDate}
                onChange={(date) => setFromDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* To Date */}
            <div className="w-[130px] mt-1 shrink-0">
              <DatePicker
                label="To Date"
                selected={toDate}
                onChange={(date) => setToDate(date ? date.toISOString().split('T')[0] : '')}
                placeholder="YYYY-MM-DD"
              />
            </div>

            {/* Rows/Page */}
            <div className="relative w-[90px] mt-1 shrink-0">
              <label className="absolute -top-2 left-2.5 bg-white px-1 text-[11px] font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Rows/Page
              </label>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-2.5 pr-6 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-1 shrink-0">
              <Button onClick={loadData} variant="secondary" className="h-[38px] px-2.5 font-semibold text-xs whitespace-nowrap">
                Apply Filters
              </Button>
            </div>

            {/* Search Input */}
            <div className="w-[180px] mt-1 shrink-0">
              <input
                placeholder="Search customer / account..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-2.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm h-[38px] px-3.5 font-bold whitespace-nowrap shrink-0"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading master data..." />}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {[
          { label: "Total Members", value: filtered.length, bg: "bg-[#FFF0C7]", border: "border-[#D97706]/25", accent: "text-[#D97706]", icon: "fa-users" },
          { label: "Total Disbursed", value: `₹${fmt(totalDisbursed)}`, bg: "bg-[#D9F3F7]", border: "border-[#0891B2]/25", accent: "text-[#0891B2]", icon: "fa-money-bill-wave" },
          { label: "Total PAR", value: `₹${fmt(totalPAR)}`, bg: "bg-[#DCEBFF]", border: "border-[#2563EB]/25", accent: "text-[#2563EB]", icon: "fa-clock" },
          { label: "Overdue Accounts", value: overdueCount, bg: "bg-[#E9DFFF]", border: "border-[#7C3AED]/25", accent: "text-[#7C3AED]", icon: "fa-user-clock" },
        ].map((c, idx) => (
          <ReportSummaryCard key={idx} {...c} />
        ))}
      </div>

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4">
          {error}
        </div>
      )}

      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table className="min-w-[3200px]">
          <Table.Head>
            <tr>
              {COLUMNS.map((c) => (
                <Table.HeadCell
                  key={c.key}
                  align={c.align || (CURRENCY_KEYS.has(c.key) ? "right" : "left")}
                  className={c.key === "address" ? "min-w-[260px] whitespace-nowrap" : "whitespace-nowrap"}
                >
                  {c.label}
                </Table.HeadCell>
              ))}
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-10">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader size="md" />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : paginated.length === 0 ? (
              <Table.EmptyRow colSpan={COLUMNS.length} message="No records found." />
            ) : (
              paginated.map((r, i) => (
                <Table.Row key={i}>
                  {COLUMNS.map((col) => (
                    <Table.Cell
                      key={col.key}
                      align={col.align || (CURRENCY_KEYS.has(col.key) ? "right" : "left")}
                      className={col.key === "address" ? "min-w-[260px] max-w-[280px] overflow-hidden whitespace-nowrap" : "whitespace-nowrap"}
                    >
                      {renderCell(r, col, i)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <span className="text-xs text-ink-500">
              Showing {paginated.length} of {filtered.length} records {filtered.length !== rows.length && `(filtered from ${rows.length})`}
            </span>
            <div className="flex gap-1 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-xs text-ink-500 px-3 font-semibold">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default MasterDataReport;
